'use client'

import { useOptionalBrandContext } from '@/contexts/brand-context'
import { useOptionalOrgContext } from '@/contexts/org-context'
import { api } from '@/convex/_generated/api'
import { useAuthedQuery } from '@/hooks/use-authed-query'
import { filterAvailableIntegrationConnections } from '@/lib/integrations/catalog-list'

/**
 * Live "connected integrations" list for the current `(orgId, brandId)`.
 *
 * Safe to call from public routes — when there is no `OrgContextProvider`
 * or `BrandContextProvider` mounted (e.g. signed-out marketing visitors
 * on `/integrations`), the Convex query is skipped and the hook returns
 * an empty list.
 *
 * Switching brands swaps the active set, matching how every other brand-
 * scoped resource (audiences, chats, automations, domains) behaves.
 */
export function useActiveIntegrations() {
  const orgContext = useOptionalOrgContext()
  const brandContext = useOptionalBrandContext()
  const orgId = orgContext?.orgId
  const brandId = brandContext?.brandId

  const activeIntegrations = useAuthedQuery(
    api.integrations.queries.listActive,
    orgId && brandId ? { orgId, brandId } : 'skip'
  )
  const isLoading =
    orgId !== undefined && Boolean(brandId) && activeIntegrations === undefined
  const availableIntegrations = filterAvailableIntegrationConnections(
    activeIntegrations ?? []
  )

  return {
    activeIntegrations: availableIntegrations,
    count: availableIntegrations.length,
    isAuthed: orgId !== undefined,
    isLoading,
  }
}
